<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { collection, getDocs, query, orderBy } from 'firebase/firestore'
import FosInput from '@/components/ui/FosInput.vue'
import FosButton from '@/components/ui/FosButton.vue'
import { db } from '@/firebase'
import { useAuthStore } from '@/stores/auth'
import { useWizardStore } from '@/stores/wizard'
import type { ObjectiveTemplate } from '@/types'

const auth = useAuthStore()
const wizard = useWizardStore()

const templates = ref<ObjectiveTemplate[]>([])
const customInput = ref('')

const filteredTemplates = computed(() => {
  const missionType = wizard.step1.mission_type
  if (!missionType) return templates.value
  return templates.value.filter((t) => t.mission_types.length === 0 || t.mission_types.includes(missionType))
})

onMounted(async () => {
  const agencyId = auth.userProfile?.agency_id
  if (!agencyId) return
  try {
    const snap = await getDocs(
      query(
        collection(db, 'agencies', agencyId, 'objective_templates'),
        orderBy('order', 'asc'),
      ),
    )
    templates.value = snap.docs.map((d) => ({ id: d.id, ...d.data() }) as ObjectiveTemplate)
  } catch {
    // Templates are optional — silently ignore if unavailable
  }
})

function addFromTemplate(text: string) {
  if (!wizard.step3.objectives.includes(text)) {
    wizard.step3.objectives.push(text)
  }
}

function addCustom() {
  const text = customInput.value.trim()
  if (!text) return
  wizard.step3.objectives.push(text)
  customInput.value = ''
}

function removeObjective(idx: number) {
  wizard.step3.objectives.splice(idx, 1)
}
</script>

<template>
  <div class="flex flex-col gap-6">
    <div>
      <h2 class="font-headline font-black text-lg uppercase tracking-tight text-on-surface">Objectives</h2>
      <p class="font-mono text-xs text-outline mt-1">Step 3 of 4 — optional</p>
    </div>

    <!-- Current objectives -->
    <div v-if="wizard.step3.objectives.length > 0" class="flex flex-col gap-2">
      <label class="font-mono text-xs uppercase tracking-widest text-outline">
        Current objectives
      </label>
      <ol class="flex flex-col gap-2">
        <li
          v-for="(obj, idx) in wizard.step3.objectives"
          :key="idx"
          class="flex items-start gap-3 bg-surface-container-low px-3 py-2.5"
        >
          <span class="font-mono text-xs text-outline font-bold pt-0.5 shrink-0">{{ idx + 1 }}.</span>
          <span class="font-mono text-sm text-on-surface flex-1">{{ obj }}</span>
          <button
            type="button"
            class="text-outline hover:text-error transition-colors shrink-0"
            @click="removeObjective(idx)"
          >
            <span class="material-symbols-outlined text-[16px]">close</span>
          </button>
        </li>
      </ol>
    </div>

    <!-- Add custom objective -->
    <div class="flex flex-col gap-1.5">
      <label class="font-mono text-xs uppercase tracking-widest text-outline">
        Add objective
      </label>
      <div class="flex gap-2">
        <FosInput
          v-model="customInput"
          placeholder="Enter objective..."
          class="flex-1"
          @keydown.enter="addCustom"
        />
        <FosButton variant="secondary" :disabled="!customInput.trim()" @click="addCustom">Add</FosButton>
      </div>
    </div>

    <!-- Templates -->
    <div v-if="filteredTemplates.length > 0" class="flex flex-col gap-2">
      <label class="font-mono text-xs uppercase tracking-widest text-outline">
        Templates
        <span v-if="wizard.step1.mission_type" class="text-outline/50 font-normal normal-case tracking-normal">
          — filtered for {{ wizard.step1.mission_type.replace('_', ' ') }}
        </span>
      </label>
      <div class="flex flex-col gap-1.5">
        <button
          v-for="template in filteredTemplates"
          :key="template.id"
          type="button"
          class="flex items-center gap-2 text-left px-3 py-2.5 border border-dashed border-border font-mono text-xs text-on-surface-variant hover:bg-surface-container-high hover:text-primary transition-colors"
          :disabled="wizard.step3.objectives.includes(template.text)"
          :class="{ 'opacity-40 cursor-not-allowed': wizard.step3.objectives.includes(template.text) }"
          @click="addFromTemplate(template.text)"
        >
          <span class="material-symbols-outlined text-[16px] shrink-0">add</span>
          {{ template.text }}
        </button>
      </div>
    </div>

    <!-- Navigation -->
    <div class="flex justify-between pt-2">
      <FosButton variant="secondary" @click="wizard.currentStep = 2">← Back</FosButton>
      <div class="flex gap-2">
        <FosButton variant="secondary" @click="wizard.currentStep = 4">Skip</FosButton>
        <FosButton @click="wizard.currentStep = 4">Next →</FosButton>
      </div>
    </div>
  </div>
</template>
